import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input, OnChanges, SimpleChanges
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

import { FormInputType } from '@forms/form-text-input/entities/input-type';
import { TravelerInfoType, TravelerTypes } from '@pax/entities/traveler-info';
import { PASSENGER_CHILDREN_RANGE_AGES, TRAVELER_FORM_INPUTS } from '@booking/constants/forms.constants';
import { isEmpty } from '@app/util/util';
import { FormInputsService } from '@reservation/services/form-inputs.service';
import { dobToDate } from '@ux/mappers/date.mapper';
import { BookingFormGeneralErrorTypes } from '@app/entities/booking-form-general-error';
import { InputMask } from '@forms/form-text-input/entities/input-mask';
import { setFormDateValue } from '@forms/mappers/form-dob.mapper';

@Component({
  selector: 'app-input-date',
  templateUrl: './input-date.component.html',
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class InputDateComponent implements OnChanges {

  @Input() form: UntypedFormGroup;
  @Input() type: TravelerTypes;
  @Input() travelerInfoType: TravelerInfoType;
  @Input() yearsRangeForChildren: number = PASSENGER_CHILDREN_RANGE_AGES.MAX;
  @Input() yearsRangeForInfants: number = PASSENGER_CHILDREN_RANGE_AGES.MIN;
  @Input() age: number;
  @Input() min: string;
  @Input() max: string;
  @Input() isMobile: boolean;
  @Input() defaultDate: string[] = [];
  @Input() usedAges = false;
  @Input() isSubmitted = false;
  @Input() isSubmittedSuccess = false;
  @Input() isRequired = true;
  @Input() invalidDate = false;
  @Input() dobSelector = TRAVELER_FORM_INPUTS.DOB;
  @Input() label = 'BOOKING_MODULE.BIRTHDAY';
  @Input() isNotABirthday = false;

  public readonly formInputType = FormInputType.CUSTOM_MASK;
  public readonly mask = InputMask.DOB;
  public readonly inputPlaceholder = 'DD/MM/AAAA';

  private formDateValue = '';
  private dateComplete = null;

  constructor(
    private readonly translate: TranslocoService,
    private readonly formInputsService: FormInputsService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.form || changes?.type) {
      this.setValues();
    }
  }

  get traveler(): string {
    const forcePassenger = this.travelerInfoType === TravelerInfoType.PACKAGE_TRAVELER ||
      this.travelerInfoType === TravelerInfoType.PERSON;
    const key = forcePassenger ? TravelerInfoType.PASSENGER : this.travelerInfoType;
    return this.translate.translate(key);
  }

  get showAgeEmptyMessageError(): boolean {
    return this.isSubmitted && (isEmpty(this.formDateValue) || this.dateComplete === null);
  }

  get isBirthDateGreaterThanCurrentDate(): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return !this.isNotABirthday && this.isSubmitted && this.dateComplete?.getTime() >= today.getTime();
  }

  get isInvalidDateValue(): boolean {
    return this.isSubmitted && !!this.form?.get(this.dobSelector).errors?.[BookingFormGeneralErrorTypes.INVALID_DATE];
  }

  get showAgeMessageErrorByAges(): boolean {
    return this.isSubmitted && !!this.form?.get(this.dobSelector).errors?.[BookingFormGeneralErrorTypes.AGE_RANGE];
  }

  get showBirthDateGreaterThanAllowed(): boolean {
    return this.isSubmitted && 
      !!this.form?.get(this.dobSelector).errors?.[BookingFormGeneralErrorTypes.BIRTHDATE_GREATER_THAN_ALLOWED];
  }

  get showBirthDateLesserThanAllowed(): boolean {
    return this.isSubmitted && 
      !!this.form?.get(this.dobSelector).errors?.[BookingFormGeneralErrorTypes.BIRTHDATE_LESSER_THAN_ALLOWED];
  }

  get ageMessageTranslation(): string {
    return this.type === TravelerTypes.ADULTS
      ? 'BOOKING_MODULE.BIRTHDATE_MINIMUM_ERROR_YEAR_ADULT'
      : this.type === TravelerTypes.CHILDREN
        ? 'BOOKING_MODULE.BIRTHDATE_RANGE_ERROR_YEARS'
        : 'BOOKING_MODULE.BIRTHDATE_RANGE_ERROR_MONTHS';
  }

  public addErrorClass(field: string): { [key: string]: boolean } {
    const ngClassObject = this.formInputsService.addErrorClass(field, this.form, this.isSubmitted);
    const ngClassValue = ngClassObject.error;
    const showError = ngClassValue || this.showAgeEmptyMessageError || this.isBirthDateGreaterThanCurrentDate ||
      this.showAgeMessageErrorByAges || this.isInvalidDateValue;
    return { ...ngClassObject, error: showError };
  }

  public setValues(): void {
    this.formDateValue = setFormDateValue(this.form?.get(this.dobSelector)?.value);
    this.form?.get(this.dobSelector)?.setValue(this.formDateValue);
    this.dateComplete = dobToDate(this.formDateValue);
  }

  public reset(): void {
    this.defaultDate = [];
    this.dateComplete = null;
    this.formDateValue = '';
    this.form?.get(this.dobSelector)?.setValue('');
  }

}
